import React, { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import CommonInput from '../../Common/CommonInput';
import CommonDate from '../../Common/CommonDate';
import CommonSelect from '../../Common/CommonSelect';
import CommonAsyncSelect from '../../Common/CommonAsyncSelect';
import TranslatableTextarea from '../../Common/TranslatableTextarea';
import { fetchSettings } from '../../../store/modules/deliverynote/deliverynoteActions';
import useFormSettings from '../../../hooks/useFormSettings';

import { 
    calculateTotals
} from './DeliveryNoteCalculation';

import jobOrderLpoService from '../../../services/joborderlpoService';
import jobCostingService from '../../../services/jobcostingService';

import useModal from '../../../hooks/useModal';
import DataSelectionModal from '../../Common/CommonModal/DataSelectionModal';
import Jobcostings from '../../../pages/Jobcostings/Jobcostings';
import { useHasPermission } from '../../../utils/permissionUtils';
const DeliveryNoteForm = ({ initialData, onSave, isLoading, onClose }) => {
    
    const hasCreatePermission = useHasPermission(['create delivery notes']);
    const hasUpdatePermission = useHasPermission(['update delivery notes']);

    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('deliverynote', fetchSettings);
    const splashData = useSplash();
    const languages = splashData?.languages || [];
    const defaultLanguage = languages.find(lang => lang.default)?.code || languages[0]?.code || 'en';
    const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);

    const [formData, setFormData] = useState({
        id: '',
        invoice_number: '',
        invoice_date: '',
        lpo_number: null,
        customer: null,
        customer_name: '',
        address1: '',
        address2: '',
        phone_number: '',
        location: null,

        delivery_rule: {},
        gross_total: '',
        discount_percentage: '',
        discount_amount: '',
        sub_total: '',
        vat_amount: '',
        net_total: '',
        advance_payment: '',
        balance: '',
        orders: [],
        items: [],
        job_costing: [],
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingLpoOrders, setIsLoadingLpoOrders] = useState(false);
    const [isLoadingOrderItems, setIsLoadingOrderItems] = useState(false);


    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                id: initialData.id || null,
                invoice_number: initialData.invoice_number || '',
                invoice_date: initialData.invoice_date || '',
                lpo_number: initialData.lpo_number || null,

                customer: initialData.customer || null,
                customer_name: initialData.customer_name || '',
                address1: initialData.address1 || '',
                address2: initialData.address2 || '',
                phone_number: initialData.phone_number || '',
                delivery_rule: initialData.delivery_rule || {},
                location: initialData.location || null,
                gross_total: initialData.gross_total || '',
                discount_percentage: initialData.discount_percentage || '',
                discount_amount: initialData.discount_amount || '',
                sub_total: initialData.sub_total || '',
                vat_amount: initialData.vat_amount || '',
                net_total: initialData.net_total || '',
                advance_payment: initialData.advance_payment || '',
                balance: initialData.balance || '',
                orders: initialData.orders || [],
                items: initialData.items || [],
                job_costing: initialData.job_costing || [],
            });
        }
    }, [initialData]);

    const calculateFormData = (updatedItems) => {
        const totals = calculateTotals(updatedItems);
        return {
            gross_total: totals.grossTotal.toFixed(3),
            discount_percentage: '',
            discount_amount: totals.discountTotal.toFixed(3),
            sub_total: (totals.grossTotal - totals.discountTotal).toFixed(3),
            vat_amount: totals.vatAmount.toFixed(3),
            net_total: totals.netTotal.toFixed(3),
        };
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSelectChange = async (name, value) => {
        let orders = [];
        if (name === 'lpo_number') {
            if (value) {
                try {
                    setIsLoadingLpoOrders(true);
                    const response = await jobOrderLpoService.getOrdersByLpo(value.id);
                    orders = response?.data;
                } catch (error) {
                    console.error('Error fetching FG items:', error);
                } finally {
                    setIsLoadingLpoOrders(false);
                }
            }
   
            setFormData(prev => ({
                ...prev,
                [name]: value ?? null,
                customer: value?.customer ?? null,
                customer_name: value?.customer_name ?? '',
                address1: value?.address1 ?? '',
                address2: value?.address2 ?? '',
                phone_number: value?.phone_number ?? '',
                orders: orders,

                job_costing: [],
                items: [],
                gross_total: '',
                discount_percentage: '',
                discount_amount: '',
                sub_total: '',
                vat_amount: '',
                net_total: '',
                advance_payment: '',
                balance: '',
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSelectOrder = async (order) => {
        let costedOrder = [];
        let items = [];
        
        try {
            setIsLoadingOrderItems(true);
            const response = await jobCostingService.getCostedOrder(order.id);
            costedOrder = response?.data;
            items = (costedOrder?.items || []).map(item => ({
                ...item,
                item_description: item?.item?.item_description ?? item?.item_description,
            }));
            
            setFormData(prev => {
                const currentJobCostings = prev.job_costing || [];
                const isOrderSelected = currentJobCostings.some(jc => jc.id === order.id);
                
                const updatedItems = isOrderSelected 
                    ? prev.items.filter(item => !items.some(removedItem => removedItem.id === item.id))
                    : [...prev.items, ...items];
                    
                const updatedJobCostings = isOrderSelected
                    ? currentJobCostings.filter(jc => jc.id !== order.id)
                    : [...currentJobCostings, order];

                const calculatedData = calculateFormData(updatedItems);
                return {
                    ...prev,
                    ...calculatedData,
                    location: order?.location ?? null,
                    items: updatedItems,
                    job_costing: updatedJobCostings,
                };
            });
        } catch (error) {
            console.error('Error fetching order costings:', error);
        } finally {
            setIsLoadingOrderItems(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isCreateMode = !formData?.id;
    const hasPermission = isCreateMode ? hasCreatePermission : hasUpdatePermission;

    const isSubmitting = isLoading || isSaving || !hasPermission;
    const selectedLang = languages.find(l => l.code === selectedLanguage);

    const jobCostingModal = useModal();
    const jobCostingExternalCondition = {
        rules: {
            condition: 'and',
            children: [
                {
                    type: 'rule',
                    query: {
                        operator: 'equal',
                        rule: 'job_costings.is_lpo',
                        type: 'text',
                        value: 1
                    }
                },
                {
                    type: 'rule',
                    query: {
                        operator: 'in',
                        rule: 'job_orders.status',
                        type: 'text',
                        value: ['4']
                    }
                }
            ]
        }
    };

    const handleJobCostingSelected = (jobCosting) => {
        const updatedValue = {
            id: jobCosting?.lpo_number,
            lpo_date: jobCosting?.lpo_date,
            label: jobCosting?.lpo_label,
            address1: jobCosting?.address1,
            address2: jobCosting?.address2,
            phone_number: jobCosting?.phone_number,
            customer: jobCosting?.customer,
            customer_name: jobCosting?.lpo_customer_name,
        }

        handleSelectChange('lpo_number', updatedValue);
        jobCostingModal.hide();
    };



    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="d-flex justify-content-between mb-2">
                <div className="d-flex justify-content-start">
                    <h5 className="mb-0">Create Delivery Note</h5>
                </div>
                <div className="d-flex justify-content-end">
                    {formData?.id && (
                        <a href={`${process.env.PUBLIC_URL}/delivery_notes/view/${formData.id}?preview=true`} target="_blank" className="btn btn-primary me-2" >
                            <i className="fas fa-print"></i> Print Preview
                        </a>
                    )}  
                </div>
            </div>
            <div className="flex-grow-1">

                <div className="row">
                    <div className="col-md-4">
                        <CommonInput
                            name="invoice_number"
                            label="Invoice Number"
                            value={formData.invoice_number}
                            onChange={handleChange}
                            placeholder="Enter invoice number"
                            disabled={true}
                            errors={errors}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonDate
                            name="invoice_date"
                            label="Invoice Date"
                            value={formData.invoice_date}
                            onChange={handleChange}
                            placeholder="Enter invoice date"
                            disabled={isSubmitting}
                            errors={errors}
                            required={true}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonSelect
                            name="customer"
                            label="Customer"
                            value={formData.customer}
                            onChange={(value) => handleSelectChange('customer', value)}
                            options={[]}
                            isSubmitting={true}
                            errors={errors}
                            required={false}
                            placeholder=""
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4">
                        <CommonAsyncSelect
                            name="lpo_number"
                            label="LPO Number"
                            cacheOptions={false}
                            value={formData.lpo_number}
                            onChange={(value) => handleSelectChange('lpo_number', value)}
                            loadOptions={async (inputValue) => {
                                const response = await jobOrderLpoService.searchCostedLpos(inputValue);
                                return response.data.map(lpo => ({
                                    id: lpo.id,
                                    label: lpo.label,
                                    customer: lpo.customer,
                                    customer_name: lpo.customer_name,
                                    address1: lpo.address1,
                                    address2: lpo.address2,
                                    phone_number: lpo.phone_number
                                }));
                            }}
                            isSubmitting={isSubmitting || formData?.id}
                            errors={errors}
                            required={true}
                            actionButtons={[
                                {
                                    icon: 'fa fa-bars',
                                    label: '',
                                    className: 'btn-primary',
                                    onClick: (value) => jobCostingModal.show()
                                }
                            ]}
                        />
                    </div>
                    <div className="col-md-4">
                        <TranslatableTextarea
                            name="customer_name"
                            label="Customer Name"
                            rows={1}
                            value={formData.customer_name}
                            onChange={handleChange}
                            errors={errors}
                            disabled={true}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="phone_number"
                            label="Phone Number"
                            value={formData.phone_number}
                            required={false}
                            disabled={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <TranslatableTextarea
                            label="Address 1"
                            name="address1"
                            rows={1}
                            value={formData.address1}
                            onChange={handleChange}
                            errors={errors}
                            disabled={true}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="address2"
                            label="Address 2"
                            value={formData.address2}
                            onChange={handleChange}
                            errors={errors}
                            disabled={true}
                            required={false}
                        />
                    </div>
                    <div className="col-md-4">
                        <CommonInput
                            name="location"
                            label="Store Location"
                            value={formData.location?.label}
                            disabled={true}
                            required={false}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-4">
                        <TranslatableTextarea
                            name="delivery_rule"
                            label="Delivery Rule"
                            rows={1}
                            value={formData.delivery_rule}
                            onChange={handleChange}
                            errors={errors}
                            disabled={isSubmitting}
                            selectedLanguage={selectedLanguage}
                            selectedLang={selectedLang}
                            required={false}
                        />
                    </div>
                </div>
                {
                    (!formData.id) && (
                        <div className="row">
                            <div className="col-md-12">
                                <h5 className="mb-3">Costed Job Orders</h5>
                                <div className="table-responsive mb-2">
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Costing Number</th>
                                                <th>Job Order Number</th>
                                                <th>Job Order Date</th>
                                                <th>Employee Name</th>
                                                <th>Customer</th>
                                                <th>Net Total</th>
                                                <th>Advance Amount</th>
                                                <th>Balance</th>
                                                <th>Trial Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.orders && formData.orders.length > 0 ? (
                                                formData.orders.map(order => (
                                                    <tr key={order.id}>
                                                        <td>
                                                            <input type="checkbox" 
                                                                className='form-check-input'
                                                                checked={formData.job_costing?.some(jc => jc.id === order.id)}
                                                                onChange={() => handleSelectOrder(order)}
                                                            />
                                                        </td>
                                                        <td className="text-nowrap">{order.costing_number}</td>
                                                        <td className="text-nowrap">{order.order_number}</td>
                                                        <td className="text-nowrap">{order.order_date}</td>
                                                        <td className="text-nowrap">{order.employee_name}</td>
                                                        <td className="text-nowrap">{order.customer?.label}</td>
                                                        <td className="text-nowrap">{order.net_total}</td>
                                                        <td className="text-nowrap">{order.advance_payment}</td>
                                                        <td className="text-nowrap">{order.balance}</td>
                                                        <td className="text-nowrap">{order.trial_date}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center">No orders available</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )
                }
                <div className="row">
                    <div className="col-md-12">
                        <h5 className="mb-3">Delivery Items</h5>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-12">
                        <div className="table-responsive mb-2">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Item Code</th>
                                        <th>Item Name</th>
                                        <th>Job Order</th>
                                        <th>Unit</th>
                                        <th>Qty</th>
                                        <th>Unit Price</th>
                                        <th>Discount %</th>
                                        <th>Line Amt.</th>
                                        <th>Header Discount</th>
                                        <th>Amount</th>
                                        <th>VAT Amt.</th>
                                        <th>Amount Incl. VAT</th>
                                        <th>VAT Prod. Group</th>
                                        <th>VAT Percentage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.items && formData.items.length > 0 ? (
                                        formData.items.map(item => (
                                            <tr key={item.id}>
                                                <td className="text-nowrap">{item?.item?.item_code}</td>
                                                <td className="text-nowrap">{item?.item_description ?? item?.item?.item_description}</td>
                                                <td className="text-nowrap">{item?.job_order_number}</td>
                                                <td className="text-nowrap">{item.unit}</td>
                                                <td className="text-nowrap">{item.quantity}</td>
                                                <td className="text-nowrap">{item.unit_price}</td>
                                                <td className="text-nowrap">{item.discount_percentage}</td>
                                                <td className="text-nowrap">{item.line_amount}</td>
                                                <td className="text-nowrap">{item.header_discount}</td>
                                                <td className="text-nowrap">{item.amount_excl_vat}</td>
                                                <td className="text-nowrap">{item.vat_amount}</td>
                                                <td className="text-nowrap">{item.amount_incl_vat}</td>
                                                <td className="text-nowrap">{item?.vat_product_group?.label}</td>
                                                <td className="text-nowrap">{item.vat_percentage}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="12" className="text-center">No items available</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="row mt-4 d-flex justify-content-end">

                    <div className="col-12 col-md-4 order-md-2">
                        <div className="mb-2 row">
                            <label className="col-lg-6 col-md-6 col-form-label">Net Total:</label>
                            <div className="col-lg-6 col-md-6">
                                <CommonInput
                                    type="number"
                                    min={0}
                                    className="mb-0 custom-style text-end"
                                    name="net_total"
                                    value={formData.net_total}
                                    onChange={handleChange}
                                    disabled={true}
                                    errors={errors}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <DataSelectionModal
                    show={jobCostingModal.isVisible}
                    onHide={jobCostingModal.hide}
                    title=""
                    DataComponent={Jobcostings}
                    selectionMode="single"
                    onDataSelected={handleJobCostingSelected}
                    componentProps={{
                        // Any props specific to Suppliers component
                        excludedActions: ['create', 'actions', 'filter'],
                        externalCondition: jobCostingExternalCondition
                    }}
                />
            </div>

            {hasPermission && (
            <div className="form-footer">
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Loading...
                        </>
                    ) : isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
            )}
        </form>
    );
};

export default DeliveryNoteForm; 